import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { DESIGN_PRESETS, type DesignPresetValues } from '@/utils/designPresets'
import { designLogoApi } from '@/services/api/superadmin'
import { getPublicSystemBranding, isSafeHttpUrl, SYSTEM_BRANDING_PUBLIC_KEY } from '@/services/api/systemBranding'
import type { SchoolDesignPublic } from '@/services/api/jumppage'
import { SchoolLoginFrame } from '@/components/jumppage/SchoolLoginFrame'

interface DesignPresetPickerProps {
  value: string
  onApplyPreset: (presetKey: string, values: DesignPresetValues) => void
}

export function DesignPresetPicker({ value, onApplyPreset }: DesignPresetPickerProps) {
  return (
    <div className="w-full max-w-2xl">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {DESIGN_PRESETS.map(preset => {
          const active = value === preset.key
          return (
            <button
              key={preset.key}
              type="button"
              onClick={() => onApplyPreset(preset.key, preset.values)}
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-left transition ${active ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
            >
              <span className="flex shrink-0 rounded overflow-hidden border border-gray-200">
                <span className="w-4 h-6" style={{ backgroundColor: preset.values.design_bg_color }} />
                <span className="w-4 h-6" style={{ backgroundColor: preset.values.design_primary_color }} />
              </span>
              <span className="text-xs font-medium text-gray-800">{preset.label}</span>
            </button>
          )
        })}
      </div>
      {value === 'custom' && (
        <p className="text-xs text-gray-500 mt-2">Eigene Farben aktiv (Preset: custom). Ein Preset wählen übernimmt wieder alle Farben.</p>
      )}
    </div>
  )
}

interface DesignLogoUploadProps {
  value: string
  onChanged: (logoData: string) => void
  onError: (msg: string) => void
}

export function DesignLogoUpload({ value, onChanged, onError }: DesignLogoUploadProps) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  const handleUpload = async (file: File) => {
    setBusy(true)
    try {
      const res = await designLogoApi.upload(file)
      onChanged(res.logo_data)
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Logo-Upload fehlgeschlagen')
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const handleDelete = async () => {
    setBusy(true)
    try {
      await designLogoApi.remove()
      onChanged('')
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Logo löschen fehlgeschlagen')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="w-full max-w-md">
      <div className="flex items-center gap-3">
        {value ? (
          <img src={value} alt="Schul-Logo" className="h-12 max-w-[180px] object-contain border border-gray-200 rounded bg-white p-1" />
        ) : (
          <span className="h-12 w-24 flex items-center justify-center text-xs text-gray-400 border border-dashed border-gray-300 rounded">Kein Logo</span>
        )}
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            className="hidden"
            onChange={e => {
              const file = e.target.files?.[0]
              if (file) void handleUpload(file)
            }}
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {busy ? 'Lädt...' : 'Logo hochladen'}
          </button>
          {value && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleDelete()}
              className="px-3 py-1.5 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
            >
              Entfernen
            </button>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-400 mt-1">Das Logo wird sofort gespeichert (unabhängig von «Speichern»). Ohne Logo gilt das bei der Schul-Anlage hinterlegte Logo.</p>
    </div>
  )
}

function designFromSchoolConfig(config: Record<string, unknown>): SchoolDesignPublic {
  const rawPos = String(config.design_logo_position || 'center')
  const logoPosition: SchoolDesignPublic['logo_position'] =
    rawPos === 'left' || rawPos === 'none' ? rawPos : 'center'
  const logoData = String(config.design_logo_data || '')
  return {
    theme: config.design_theme === 'dark' ? 'dark' : 'light',
    logo_position: logoPosition,
    show_school_name: config.design_show_school_name !== false,
    logo_data: logoData || null,
    logo_url: String(config.design_logo_url || '') || null,
    primary_color: String(config.design_primary_color || '#2563eb'),
    bg_color: String(config.design_bg_color || '#f9fafb'),
    text_color: String(config.design_text_color || '#111827'),
    muted_color: String(config.design_muted_color || '#4b5563'),
    button_bg_color: String(config.design_button_bg_color || '#2563eb'),
    button_text_color: String(config.design_button_text_color || '#ffffff'),
    button_hover_color: String(config.design_button_hover_color || '#1d4ed8'),
    border_color: String(config.design_border_color || '#d1d5db'),
  }
}

interface DesignPreviewProps {
  config: Record<string, unknown>
}

export function DesignPreview({ config }: DesignPreviewProps) {
  const { data: systemBranding } = useQuery({
    queryKey: SYSTEM_BRANDING_PUBLIC_KEY,
    queryFn: getPublicSystemBranding,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
  const design = designFromSchoolConfig(config)
  const surface = design.theme === 'dark' ? design.bg_color : '#ffffff'
  const title = String(config.login_page_title || 'Login')
  const subtitle = String(config.login_page_subtitle || '')
  const hint = String(config.login_page_hint_text || '')
  const systemLogo =
    systemBranding?.email_logo_png ||
    systemBranding?.email_logo_svg ||
    systemBranding?.logo_png ||
    systemBranding?.logo_svg ||
    null
  const systemLogoUrl = isSafeHttpUrl(systemBranding?.url) ? systemBranding?.url : null

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-600">Vorschau (Login-Seite)</div>
      <div className="pointer-events-none">
        <SchoolLoginFrame
          design={design}
          schoolName="Schulname"
          title={title}
          subtitle={subtitle}
          systemLogo={systemLogo}
          systemLogoUrl={systemLogoUrl}
          compact
        >
          <div className="mt-8 space-y-6">
            <div
              className="appearance-none rounded-md relative block w-full px-3 py-2 border sm:text-sm"
              style={{ backgroundColor: surface, borderColor: design.border_color, color: design.text_color }}
            >
              E-Mail
            </div>
            <button
              type="button"
              tabIndex={-1}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md"
              style={{ backgroundColor: design.button_bg_color, color: design.button_text_color }}
            >
              Login-Link senden
            </button>
            {hint ? <p className="text-xs text-center" style={{ color: design.muted_color }}>{hint}</p> : null}
          </div>
        </SchoolLoginFrame>
      </div>
    </div>
  )
}
